/*
 * Copyright (C) 2019 Spanning Cloud Apps.  All rights reserved.
 */

import './uploadView.tpl.html';

angular
  .module('bulkExport.uploadView', [
    'filters.i18n',
    'services.batch',
    'services.bulkrestore',
    'services.bulkrestoredata',
    'services.error',
    'services.useragent',
    'shared.wizard',
    'sbsf.enums'
  ])
  /**
   * Controller for the bulkRestore.uploadView template.
   */
  .controller('BulkExportUploadViewCtrl', [
    '$scope',
    '$filter',
    '$rootScope',
    '$state',
    'batchUtils',
    'httpBulkRestore',
    'httpQueue',
    'progressFactory',
    'BulkRestoreService',
    'ErrorService',
    'UserAgentFactory',
    'WizardButtonFactory',
    'WizardService',
    'VIEW_STATES',
    'JOB_TYPE',
    'ALERT_EVENTS',
    function (
      $scope,
      $filter,
      $rootScope,
      $state,
      batchUtils,
      httpBulkRestore,
      httpQueue,
      progressFactory,
      BulkRestoreService,
      ErrorService,
      UserAgentFactory,
      WizardButtonFactory,
      WizardService,
      VIEW_STATES,
      JOB_TYPE,
      ALERT_EVENTS
    ) {
      if (!$state.params.bulkRestoreData) {
        throw new Error('bulkRestoreData is required');
      }

      const i18n = $filter('i18n');
      const bulkRestoreData = $state.params.bulkRestoreData;
      let touched, model;

      const defaultSelectedBatch = {
        date: new Date()
      };

      // Published functions
      $scope.hasEnoughBackups = hasEnoughBackups;
      $scope.shouldShowUploadInstructions = shouldShowUploadInstructions;
      $scope.submitBackup = submitBackup;
      $scope.prepareFileAndQueueMessage = prepareFileAndQueueMessage;

      // Publish the bulk restore data
      $scope.bulkRestoreData = bulkRestoreData;

      // Store data in a model object rather than the scope itself for easier watching
      $scope.model = model = {
        hasUploadFile: false,
        // For the purposes of confirming cancellation of the wizard we'll consider the wizard touched if we get this
        // far.
        touched: true,
        uploadBatchId: undefined,
        uploadFile: undefined,
        uploadSupported: !UserAgentFactory.isIE9(),
        selectMode: false,
        selectedBatch: defaultSelectedBatch
      };

      initWizardPage();
      watchRequestsInProgress();
      watchUploadBatchId();
      watchUploadFile();
      watchSelectedBatch();

      $scope.switchSelectMode = function (value) {
        model.selectMode = value;
        if (!model.selectMode) {
          bulkRestoreData.setUploadedCsv(null);
        } else {
          initBatch();
        }
      };

      /**
       * Determines whether there are enough backups to run a restore.
       *
       * @returns {boolean} true if there are enough backups.
       */
      function hasEnoughBackups() {
        return batchUtils.backupBatchDots.count > 1;
      }

      /**
       * Sets up this page for use with the wizard service.
       */
      function initWizardPage() {
        // Create a custom wizard button to the wizard to handle Verify
        const verifyButton = WizardButtonFactory.generate();
        verifyButton.setTextKey('VERIFY');
        verifyButton.setTooltipKey('VERIFY');
        verifyButton.setClick(prepareFileAndQueueMessage);
        // Can only verify after CSV file is uploaded
        verifyButton.setEnabled(() => {
          const csvPrepared = !model.selectMode ? model.hasUploadFile : Boolean(bulkRestoreData.getUploadedCsv());
          return csvPrepared && !progressFactory.bulkRestoreCsvVerificationInProgress;
        });

        // Register our page with the wizard service
        const page = WizardService.getCurrentPage();
        page.setHeaderText(i18n('UPLOAD_OR_SELECT_AND_VERIFY_CSV'));
        page.setStateParams($state.params);
        page.setCustomButtons(() => [verifyButton]);
        page.setTouched(() => touched);
      }

      /**
       * Determines whether to display the instructions for upload.
       *
       * @returns {boolean} true if the upload instructions are to be shown.
       */
      function shouldShowUploadInstructions() {
        return model.uploadSupported && !$scope.showMaskWithDashboardButton;
      }

      /**
       * Submit a backup to be queued.
       */
      function submitBackup() {
        $rootScope.showLoadingMask = true;

        return httpQueue
          .submitBackup()
          .then(() => {
            progressFactory.backupInProgress = true;

            // Reset the touched flag so we can go to the dashboard without confirmation
            touched = false;
            $state.go(VIEW_STATES.ADMIN_HOME);
          })
          .catch((err) => {
            ErrorService.report(err, i18n('SUBMIT_BACKUP_FAILED'));
            progressFactory.backupInProgress = false;
          })
          .finally(() => {
            $rootScope.showLoadingMask = false;
          });
      }

      /**
       * Function to upload CSV file to s3 or use uploaded csv file and queue message
       *
       * @returns {Promise} a promise that is resolved with one of the following:
       *   1) true if the wizard can be dismissed
       *   2) false if the wizard should remain
       *   3) an object containing the parameters to pass to the wizard cancel function to display an error message.
       */
      function prepareFileAndQueueMessage() {
        $rootScope.showLoadingMask = true;

        // Do the upload and verification in two separate steps so we can display the proper error.
        let uploaded = false;
        let timestamp;
        let prepareCsvPromise;

        if (!model.selectMode) {
          timestamp = new Date().getTime();
          prepareCsvPromise = BulkRestoreService.getPreSignedPostUrl(timestamp)
            .then((url) => httpBulkRestore.uploadFileToUrl(url, model.uploadFile))
            .then(() =>
              httpBulkRestore.saveUploadedFileInfo(timestamp, bulkRestoreData.getUploadFileName(), JOB_TYPE.EXPORT)
            );
        } else {
          const csv = bulkRestoreData.getUploadedCsv();
          timestamp = csv.timestamp;
          bulkRestoreData.setUploadFile({
            name: csv.name
          });
          prepareCsvPromise = Promise.resolve();
        }

        return prepareCsvPromise
          .then(() => {
            uploaded = true;
            return BulkRestoreService.submitCsvVerification(bulkRestoreData, timestamp, JOB_TYPE.EXPORT);
          })
          .then(() => {
            // When the verification starts successfully, switch to the dashboard mask
            $rootScope.showLoadingMask = false;
            $rootScope.showMaskWithDashboardButton = true;

            progressFactory.bulkRestoreCsvVerificationInProgress = true;

            // Reset the touched flag so the user can exit to the dashboard without another confirmation
            touched = false;

            return false; // Don't close the wizard yet since we're allowing the user to wait here.
          })
          .catch((err) => {
            $rootScope.showLoadingMask = false;

            // We're back to needing confirmation to exit
            touched = true;

            ErrorService.report(err, uploaded ? i18n('ERROR_OCCURRED') : i18n('UPLOAD_FILE_ERROR'));

            return false; // Stay in the wizard since something went wrong
          });
      }

      /**
       * Upload initial batch for choose
       */
      function initBatch() {
        if (!model.selectedBatch || !model.selectedBatch.batchId) {
          $rootScope.showLoadingMask = true;
          batchUtils
            .getBatches(1, true)
            .then(
              function (batches) {
                if (batches.rows.length > 0) {
                  $scope.model.selectedBatch = batches.rows[0];
                }
              },
              function () {
                $rootScope.$emit(ALERT_EVENTS.ERROR.eventName, $filter('i18n')('ERROR_OCCURRED'));
              }
            )
            .finally(() => {
              $rootScope.showLoadingMask = false;
            });
        }
      }

      /**
       * Watches for a change to the number of server requests currently in progress.
       */
      function watchRequestsInProgress() {
        $scope.$watch(
          () => batchUtils.requestsInProgress,
          (newVal, oldVal) => {
            if (newVal !== oldVal) {
              $rootScope.showLoadingMask = newVal > 0;
            }
          }
        );
      }

      /**
       * Watches for a change to the batch specified by the CSV upload batch picker.
       */
      function watchUploadBatchId() {
        $scope.$watch(
          () => model.uploadBatchId,
          (newVal) => {
            if (newVal > 0) {
              bulkRestoreData.setBackupBatchId(model.uploadBatchId);
            }
          }
        );
      }

      /**
       * Watches for a change to the selected CSV file.
       */
      function watchUploadFile() {
        $scope.$watch(
          () => model.uploadFile,
          (newVal) => {
            if (newVal) {
              bulkRestoreData.setUploadFile(model.uploadFile);
            }
          }
        );
      }

      /**
       * Watches for a change to the selected batch specified by the CSV selection batch picker.
       */
      function watchSelectedBatch() {
        $scope.$watch(
          () => model.selectedBatch,
          (newVal) => {
            if (newVal) {
              bulkRestoreData.setBackupBatchId(model.selectedBatch.batchId);
            }
          }
        );
      }
    }
  ]);
